/**
 * <be-gallery>: product media gallery built from every product media item.
 * Native scroll-snap swipe, arrows, thumbnails, counter, desktop hover zoom and a
 * fullscreen <dialog> lightbox with tap-to-zoom and keyboard navigation.
 */
import { motionFactor, lockScroll } from '@be/core';

class BeGallery extends HTMLElement {
  /** @type {AbortController | null} */ #ac = null;
  index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.track = /** @type {HTMLElement|null} */ (this.querySelector('[data-track]'));
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dialog = /** @type {HTMLDialogElement|null} */ (this.querySelector('dialog'));
    this.lbTrack = /** @type {HTMLElement|null} */ (this.dialog?.querySelector('[data-lb-track]') || null);
    if (!this.track || this.slides.length === 0) return;

    let raf = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.#syncFromScroll());
      },
      { passive: true, signal }
    );
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.#onKey(event), { signal });

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches && motionFactor() >= 0) {
      this.track.addEventListener('pointermove', (event) => this.#zoom(event), { signal });
      this.track.addEventListener('pointerleave', () => this.#unzoom(), { signal });
    }

    if (this.lbTrack) {
      let lraf = 0;
      this.lbTrack.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(lraf);
          lraf = requestAnimationFrame(() => {
            const i = this.#indexOf(this.lbTrack);
            this.#counter(i);
          });
        },
        { passive: true, signal }
      );
      this.dialog?.addEventListener(
        'close',
        () => {
          lockScroll(this, false);
          this.go(this.#indexOf(this.lbTrack), false);
        },
        { signal }
      );
    }

    document.addEventListener(
      'be:selection-change',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        if (!detail?.variant?.media || detail.productId !== this.dataset.productId) return;
        const i = this.slides.findIndex((s) => s.getAttribute('data-media-id') === String(detail.variant.media));
        if (i >= 0 && i !== this.index) this.go(i);
      },
      { signal }
    );
    this.#syncFromScroll();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    lockScroll(this, false);
  }

  closeLightbox() {
    lockScroll(this, false);
    if (this.dialog?.open) this.dialog.close();
  }

  #indexOf(track) {
    if (!track || !track.clientWidth) return 0;
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  #syncFromScroll() {
    const i = Math.min(this.slides.length - 1, this.#indexOf(this.track));
    if (i === this.index && this.dataset.ready) return;
    this.dataset.ready = '1';
    this.index = i;
    this.#unzoom();
    this.querySelectorAll('[data-go]').forEach((button) => {
      const active = Number(button.getAttribute('data-go')) === i;
      button.setAttribute('aria-current', active ? 'true' : 'false');
      if (active && button.closest('[data-thumbs]')) {
        const strip = button.closest('[data-thumbs]');
        if (strip instanceof HTMLElement && button instanceof HTMLElement) {
          const left = button.offsetLeft - strip.clientWidth / 2 + button.clientWidth / 2;
          strip.scrollTo({ left, behavior: motionFactor() ? 'smooth' : 'auto' });
        }
      }
    });
    this.slides.forEach((slide, n) => slide.setAttribute('aria-hidden', n === i ? 'false' : 'true'));
    this.#counter(i);
  }

  #counter(i) {
    const total = this.slides.length;
    this.querySelectorAll('[data-counter]').forEach((el) => {
      el.textContent = `${String(i + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
    });
  }

  /** @param {number} i @param {boolean} [smooth] */
  go(i, smooth = true) {
    if (!this.track) return;
    const n = this.slides.length;
    const target = ((i % n) + n) % n;
    this.track.scrollTo({ left: target * this.track.clientWidth, behavior: smooth && motionFactor() ? 'smooth' : 'auto' });
    if (!smooth) {
      this.index = -1;
      this.#syncFromScroll();
    }
  }

  openLightbox(i = this.index) {
    if (!this.dialog || !this.lbTrack) return;
    this.dialog.showModal();
    lockScroll(this, true);
    requestAnimationFrame(() => {
      if (!this.lbTrack) return;
      this.lbTrack.scrollTo({ left: i * this.lbTrack.clientWidth, behavior: 'auto' });
      this.#counter(i);
    });
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const go = target.closest('[data-go]');
    if (go) return this.go(Number(go.getAttribute('data-go')));
    if (target.closest('[data-prev]')) return this.go(this.index - 1);
    if (target.closest('[data-next]')) return this.go(this.index + 1);
    if (target.closest('[data-open-lightbox]')) return this.openLightbox();
    if (target.closest('[data-lb-close]')) return this.closeLightbox();
    if (target.closest('[data-lb-prev]') || target.closest('[data-lb-next]')) {
      const dir = target.closest('[data-lb-prev]') ? -1 : 1;
      return this.#lbStep(dir);
    }
    const lbImage = target.closest('[data-lb-zoom]');
    if (lbImage instanceof HTMLElement) {
      const zoomed = lbImage.classList.toggle('is-zoomed');
      if (zoomed) {
        const r = lbImage.getBoundingClientRect();
        lbImage.style.setProperty('--ox', `${((event.clientX - r.left) / r.width) * 100}%`);
        lbImage.style.setProperty('--oy', `${((event.clientY - r.top) / r.height) * 100}%`);
      }
      return;
    }
    if (target.closest('[data-slide]') && !target.closest('a, button')) this.openLightbox();
  }

  #lbStep(dir) {
    if (!this.lbTrack) return;
    const n = this.slides.length;
    const i = (((this.#indexOf(this.lbTrack) + dir) % n) + n) % n;
    this.lbTrack.querySelectorAll('.is-zoomed').forEach((el) => el.classList.remove('is-zoomed'));
    this.lbTrack.scrollTo({ left: i * this.lbTrack.clientWidth, behavior: motionFactor() ? 'smooth' : 'auto' });
  }

  /** @param {KeyboardEvent} event */
  #onKey(event) {
    if (this.dialog?.open) {
      if (event.key === 'ArrowRight') this.#lbStep(1);
      if (event.key === 'ArrowLeft') this.#lbStep(-1);
      return;
    }
    if (!(event.target instanceof Element) || !event.target.closest('[data-track], [data-thumbs]')) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.go(this.index + 1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.go(this.index - 1);
    }
  }

  /** @param {PointerEvent} event */
  #zoom(event) {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element) || event.target.closest('button')) return this.#unzoom();
    const slide = this.slides[this.index];
    const layer = /** @type {HTMLElement|null} */ (slide?.querySelector('[data-zoom]') || null);
    if (!layer) return;
    const r = slide.getBoundingClientRect();
    layer.style.transformOrigin = `${((event.clientX - r.left) / r.width) * 100}% ${((event.clientY - r.top) / r.height) * 100}%`;
    layer.style.transform = 'scale(1.9)';
  }

  #unzoom() {
    this.querySelectorAll('[data-zoom]').forEach((el) => {
      if (el instanceof HTMLElement) el.style.transform = '';
    });
  }
}

if (!customElements.get('be-gallery')) customElements.define('be-gallery', BeGallery);